// Enquiry to Invoice API (backend mounted at /api/e2i). Every JSON call answers { success, data }; these helpers
// return `data`, with every list the screens read made an array (a missing one is empty), so the screens can rely
// on the contract's shapes. SAP reads and writes can take a while, hence the longer timeouts.
import apiService from './api.service';
import type {
  ApprovalView,
  Attachment,
  AttachmentEntity,
  BulkSubmitResult,
  CalJobInput,
  Cir,
  CirSaveInput,
  CustomerOption,
  E2IConfig,
  E2ISettings,
  E2ISetupData,
  EnquiryFilters,
  EnquiryInput,
  EnquiryRow,
  EnquiryWorkspace,
  EstimateSaveInput,
  JobFieldsInput,
  JobSaveInput,
  PartyRef,
  ProjectOption,
  QuotationSaveInput,
  RegisterCode,
  RegisterFilters,
  RegisterRow,
  UserOption,
  WorkspacePermissions,
} from '../types/e2i';
import { emptyE2IConfig } from '../utils/e2i';

type Envelope<T> = { success: boolean; data: T; message?: string };
const data = <T>(res: Envelope<T>): T => res.data;
const BASE = '/e2i';
const SAP_TIMEOUT = 120000;
const SAP = { timeout: SAP_TIMEOUT };

/** The shared axios client (auth header, 401 handling) for what apiService has no helper for. */
const http = () => (apiService as any).axiosInstance;

/** Query parameters without the empty ones. */
const params = (p: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== null && String(v).trim() !== '') out[k] = v;
  return out;
};

// ---------------------------------------------------------------- answers made safe to read

const arr = <T>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);

const NO_WORKSPACE_PERMISSIONS: WorkspacePermissions = {
  canEdit: false, canPost: false, canCancel: false, canEstimate: false, canQuote: false,
  canDecideQuotation: false, canSendQuotation: false, canRecordClientDecision: false,
  canCreateJob: false, canDecideJob: false, canCreateOrder: false, canCompleteJob: false,
  canCreateCir: false, canSetRegister: false,
};

const NO_CIR_PERMISSIONS: Cir['permissions'] = { canEdit: false, canSubmit: false, canBook: false, canReturn: false, canCancel: false, canSetClientDate: false };

const approvalOf = (a: ApprovalView | null | undefined): ApprovalView => ({
  stages: arr(a?.stages).map((s) => ({ ...s, approvers: arr(s.approvers) })),
  currentStep: a?.currentStep ?? null,
});

export const normalizeCir = (c: Cir): Cir => ({
  ...c,
  lines: arr(c.lines),
  attachments: arr(c.attachments),
  permissions: { ...NO_CIR_PERMISSIONS, ...(c.permissions || {}) },
});

export const normalizeWorkspace = (w: EnquiryWorkspace): EnquiryWorkspace => ({
  ...w,
  enquiry: { ...w.enquiry, activities: arr(w.enquiry?.activities) },
  estimates: arr(w.estimates).map((e) => ({ ...e, lines: arr(e.lines) })),
  quotations: arr(w.quotations).map((q) => ({ ...q, markups: q.markups || {}, lines: arr(q.lines), approval: approvalOf(q.approval) })),
  jobs: arr(w.jobs).map((j) => ({ ...j, lines: arr(j.lines), approval: approvalOf(j.approval) })),
  cirs: arr(w.cirs).map(normalizeCir),
  attachments: arr(w.attachments),
  history: arr(w.history),
  checks: arr(w.checks),
  permissions: { ...NO_WORKSPACE_PERMISSIONS, ...(w.permissions || {}) },
});

export const normalizeConfig = (c: E2IConfig, companyId: number): E2IConfig => {
  const base = emptyE2IConfig(companyId);
  return {
    ...base,
    ...c,
    capabilities: { ...base.capabilities, ...(c?.capabilities || {}) },
    costTypes: arr(c?.costTypes),
    vatCodes: arr(c?.vatCodes),
    jobTypes: arr(c?.jobTypes),
    paymentTerms: arr(c?.paymentTerms),
    registerCodes: arr(c?.registerCodes),
  };
};

const normalizeSetup = (s: E2ISetupData): E2ISetupData => ({
  roles: arr(s?.roles),
  settings: {
    ...s.settings,
    costTypes: arr(s.settings?.costTypes),
    jobTypes: arr(s.settings?.jobTypes),
    quotationStages: arr(s.settings?.quotationStages),
    calStages: arr(s.settings?.calStages),
  },
});

// ---------------------------------------------------------------- transport

async function put<T>(url: string, body: unknown): Promise<T> {
  const res = await http().put(url, body, { timeout: SAP_TIMEOUT });
  return data(res.data as Envelope<T>);
}

async function blob(url: string, query?: Record<string, unknown>): Promise<Blob> {
  const res = await http().get(url, { params: query, responseType: 'blob', timeout: SAP_TIMEOUT });
  return res.data as Blob;
}

const post = async <T>(url: string, body: unknown = {}): Promise<T> => data(await apiService.post<Envelope<T>>(url, body, SAP));
const get = async <T>(url: string, query?: Record<string, unknown>): Promise<T> => data(await apiService.get<Envelope<T>>(url, query, SAP));

/** A call that answers the enquiry workspace. */
const wsOf = async (call: Promise<EnquiryWorkspace>): Promise<EnquiryWorkspace> => normalizeWorkspace(await call);
/** A call that answers one invoice request. */
const cirOf = async (call: Promise<Cir>): Promise<Cir> => normalizeCir(await call);

const e2iApi = {
  // ---- configuration and pickers
  config: async (companyId: number) => normalizeConfig(await get<E2IConfig>(`${BASE}/config`, { companyId }), companyId),
  customers: async (companyId: number, q: string) => arr(await get<CustomerOption[]>(`${BASE}/lookups/customers`, params({ companyId, q }))),
  /** Projects matching q; with `parent`, the children of that main project. */
  projects: async (companyId: number, q: string, parent?: string | null) => arr(await get<ProjectOption[]>(`${BASE}/lookups/projects`, params({ companyId, q, parent }))),
  /** Sales items (document mode Item). */
  items: async (companyId: number, q: string) => arr(await get<PartyRef[]>(`${BASE}/lookups/items`, params({ companyId, q }))),
  /** Revenue accounts (document mode Service). */
  accounts: async (companyId: number, q: string) => arr(await get<PartyRef[]>(`${BASE}/lookups/accounts`, params({ companyId, q }))),
  users: async (companyId: number, q: string) => arr(await get<UserOption[]>(`${BASE}/lookups/users`, params({ companyId, q }))),

  // ---- enquiries
  listEnquiries: async (companyId: number, f: EnquiryFilters = {}) =>
    arr(await get<EnquiryRow[]>(`${BASE}/enquiries`, params({ companyId, status: f.status, register: f.register, q: f.q, from: f.from, to: f.to }))),
  createEnquiry: (companyId: number, input: EnquiryInput) => wsOf(post<EnquiryWorkspace>(`${BASE}/enquiries`, { companyId, ...input })),
  workspace: (enquiryId: number) => wsOf(get<EnquiryWorkspace>(`${BASE}/enquiries/${enquiryId}`)),
  /** The same fields as the create (the company cannot change). */
  saveEnquiry: (enquiryId: number, companyId: number, input: EnquiryInput) => wsOf(put<EnquiryWorkspace>(`${BASE}/enquiries/${enquiryId}`, { companyId, ...input })),
  postEnquiry: (enquiryId: number) => wsOf(post<EnquiryWorkspace>(`${BASE}/enquiries/${enquiryId}/post`)),
  cancelEnquiry: (enquiryId: number, reason: string) => wsOf(post<EnquiryWorkspace>(`${BASE}/enquiries/${enquiryId}/cancel`, { reason })),
  setRegister: (enquiryId: number, code: RegisterCode, note: string) => wsOf(post<EnquiryWorkspace>(`${BASE}/enquiries/${enquiryId}/register`, { code, note })),

  // ---- estimates
  /** A new estimate revision (the first one, or a copy of the latest). */
  newEstimate: (enquiryId: number) => wsOf(post<EnquiryWorkspace>(`${BASE}/enquiries/${enquiryId}/estimates`)),
  saveEstimate: (estimateId: number, input: EstimateSaveInput) => wsOf(put<EnquiryWorkspace>(`${BASE}/estimates/${estimateId}`, input)),
  postEstimate: (estimateId: number) => wsOf(post<EnquiryWorkspace>(`${BASE}/estimates/${estimateId}/post`)),
  /** A new quotation revision from a posted estimate (its lines with the default markups). */
  quotationFromEstimate: (estimateId: number) => wsOf(post<EnquiryWorkspace>(`${BASE}/estimates/${estimateId}/quotations`)),

  // ---- quotations
  saveQuotation: (quotationId: number, input: QuotationSaveInput) => wsOf(put<EnquiryWorkspace>(`${BASE}/quotations/${quotationId}`, input)),
  submitQuotation: (quotationId: number) => wsOf(post<EnquiryWorkspace>(`${BASE}/quotations/${quotationId}/submit`)),
  decideQuotation: (quotationId: number, action: 'approve' | 'reject' | 'revert', comment: string | null) =>
    wsOf(post<EnquiryWorkspace>(`${BASE}/quotations/${quotationId}/decision`, { action, comment })),
  /** Posts the SAP Sales Quotation and marks it sent; also the retry after SAP refused it. */
  sendQuotation: (quotationId: number) => wsOf(post<EnquiryWorkspace>(`${BASE}/quotations/${quotationId}/send`)),
  clientDecision: (quotationId: number, body: { decision: 'approved' | 'rejected'; poRef: string | null; poDate: string | null; note: string | null }) =>
    wsOf(post<EnquiryWorkspace>(`${BASE}/quotations/${quotationId}/client`, body)),
  reviseQuotation: (quotationId: number) => wsOf(post<EnquiryWorkspace>(`${BASE}/quotations/${quotationId}/revise`)),
  quotationPdf: (quotationId: number) => blob(`${BASE}/quotations/${quotationId}/pdf`),

  // ---- jobs
  /** A job from the client-approved quotation (CAE): its priced lines. */
  jobFromQuotation: (quotationId: number, input: JobFieldsInput) => wsOf(post<EnquiryWorkspace>(`${BASE}/quotations/${quotationId}/jobs`, input)),
  /** A job without a quotation (CAL): a reason and typed lines. */
  jobWithoutQuotation: (enquiryId: number, input: CalJobInput) => wsOf(post<EnquiryWorkspace>(`${BASE}/enquiries/${enquiryId}/jobs`, input)),
  saveJob: (jobId: number, input: JobSaveInput) => wsOf(put<EnquiryWorkspace>(`${BASE}/jobs/${jobId}`, input)),
  submitJob: (jobId: number) => wsOf(post<EnquiryWorkspace>(`${BASE}/jobs/${jobId}/submit`)),
  decideJob: (jobId: number, action: 'approve' | 'reject' | 'revert', comment: string | null) =>
    wsOf(post<EnquiryWorkspace>(`${BASE}/jobs/${jobId}/decision`, { action, comment })),
  /** Makes the SAP Sales Order draft that finance adds in SAP; also the retry. */
  createOrder: (jobId: number) => wsOf(post<EnquiryWorkspace>(`${BASE}/jobs/${jobId}/order`)),
  completeJob: (jobId: number) => wsOf(post<EnquiryWorkspace>(`${BASE}/jobs/${jobId}/complete`)),
  createCir: (jobId: number, input: CirSaveInput) => wsOf(post<EnquiryWorkspace>(`${BASE}/jobs/${jobId}/cirs`, input)),

  // ---- client invoice requests
  listCirs: async (companyId: number, status?: string, q?: string) => arr(await get<Cir[]>(`${BASE}/cirs`, params({ companyId, status, q }))).map(normalizeCir),
  saveCir: (cirId: number, input: CirSaveInput) => cirOf(put<Cir>(`${BASE}/cirs/${cirId}`, input)),
  submitCir: (cirId: number) => cirOf(post<Cir>(`${BASE}/cirs/${cirId}/submit`)),
  async bulkSubmitCirs(companyId: number, cirIds: number[]): Promise<BulkSubmitResult> {
    const r = await post<BulkSubmitResult>(`${BASE}/cirs/bulk-submit`, { companyId, cirIds });
    return { submitted: arr(r?.submitted), failed: arr(r?.failed) };
  },
  /** Finance: makes the SAP A/R invoice draft; also the retry. */
  bookCir: (cirId: number) => cirOf(post<Cir>(`${BASE}/cirs/${cirId}/book`)),
  returnCir: (cirId: number, note: string) => cirOf(post<Cir>(`${BASE}/cirs/${cirId}/return`, { note })),
  cancelCir: (cirId: number, reason: string) => cirOf(post<Cir>(`${BASE}/cirs/${cirId}/cancel`, { reason })),
  clientSubmission: (cirId: number, date: string) => cirOf(post<Cir>(`${BASE}/cirs/${cirId}/client-submission`, { date })),

  // ---- files
  async uploadAttachment(entity: AttachmentEntity, entityId: number, file: File): Promise<Attachment> {
    const form = new FormData();
    form.append('file', file);
    form.append('entity', entity);
    form.append('entityId', String(entityId));
    const res = await http().post(`${BASE}/attachments`, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: SAP_TIMEOUT });
    return data(res.data as Envelope<Attachment>);
  },
  downloadAttachment: (attachmentId: number) => blob(`${BASE}/attachments/${attachmentId}`),
  async deleteAttachment(attachmentId: number): Promise<void> {
    await apiService.delete<Envelope<unknown>>(`${BASE}/attachments/${attachmentId}`);
  },

  // ---- register
  register: async (companyId: number, f: RegisterFilters = {}) =>
    arr(await get<RegisterRow[]>(`${BASE}/register`, params({ companyId, ...f }))).map((r) => ({ ...r, invoiceNums: arr(r.invoiceNums) })),
  registerExport: (companyId: number, f: RegisterFilters = {}) => blob(`${BASE}/register/export`, params({ companyId, ...f })),

  // ---- setup
  setup: async (companyId: number) => normalizeSetup(await get<E2ISetupData>(`${BASE}/setup`, { companyId })),
  saveSetup: async (companyId: number, settings: E2ISettings) => normalizeSetup(await put<E2ISetupData>(`${BASE}/setup/settings`, { companyId, ...settings })),
};

export default e2iApi;
